import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API_BASE = `${BACKEND_URL}/api`;

// Create axios instance with credentials support for httpOnly cookies
const api = axios.create({
  baseURL: API_BASE,
  headers: {
    'Content-Type': 'application/json',
  },
  withCredentials: true, // Send cookies with cross-origin requests
});

// Break-glass support session: when a platform admin opens a support
// session, we persist its id in sessionStorage. The request interceptor
// below attaches it to every outgoing API call so the backend's
// `get_tenant_scope` can override the tenant to the targeted org.
export const BREAK_GLASS_STORAGE_KEY = 'elara.break_glass.session_id';
export function getBreakGlassSessionId() {
  try {
    return sessionStorage.getItem(BREAK_GLASS_STORAGE_KEY);
  } catch {
    return null;
  }
}
export function setBreakGlassSessionId(id) {
  try {
    if (id) sessionStorage.setItem(BREAK_GLASS_STORAGE_KEY, id);
    else sessionStorage.removeItem(BREAK_GLASS_STORAGE_KEY);
  } catch {
    /* storage unavailable — degrade silently */
  }
}

api.interceptors.request.use((config) => {
  const sid = getBreakGlassSessionId();
  if (sid) {
    // Scoped to this axios call only — do not mutate the shared default.
    config.headers = config.headers || {};
    config.headers['X-Break-Glass-Session'] = sid;
  }
  return config;
});

// Response interceptor to handle errors
api.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      // Break-glass expired/revoked while a request was in flight → clear
      // the stored session id so the banner & UI update immediately.
      const detail = (error.response?.data?.detail || '').toLowerCase();
      if (detail.includes('support session')) {
        setBreakGlassSessionId(null);
      }

      // Don't redirect for /auth/me (session check) or if already on login page
      const isAuthMeCheck = error.config?.url?.includes('/auth/me');
      const onLoginPage = window.location.pathname === '/login';

      if (!isAuthMeCheck && !onLoginPage && !detail.includes('support session')) {
        window.location.href = '/login';
      }
    }
    return Promise.reject(error);
  }
);

// Auth API
export const authAPI = {
  login: (data) => api.post('/auth/login', data),
  register: (data) => api.post('/auth/register', data),
  getMe: () => api.get('/auth/me'),
  logout: () => api.post('/auth/logout'),
  // Role Selection (multi-role users)
  selectRole: (role) => api.post('/auth/select-role', { role }),
  // Role Impersonation (Admin only)
  impersonate: (role) => api.post('/auth/impersonate', { role }),
  stopImpersonate: () => api.post('/auth/stop-impersonate'),
  getImpersonationStatus: () => api.get('/auth/impersonation-status'),
  // MFA (TOTP)
  mfaStatus: () => api.get('/auth/mfa/status'),
  mfaSetup: () => api.post('/auth/mfa/setup'),
  mfaVerifySetup: (code) => api.post('/auth/mfa/verify-setup', { code }),
  mfaVerifyLogin: (mfa_token, code) => api.post('/auth/mfa/verify', { mfa_token, code }),
  mfaDisable: (code) => api.post('/auth/mfa/disable', { code }),
};

// Trial Lifecycle API
export const trialAPI = {
  getStatus: () => api.get('/trial/status'),
  runExpiry: () => api.post('/trial/run-expiry'),
  runReminders: () => api.post('/trial/run-reminders'),
};

// Payments / Billing API
export const paymentsAPI = {
  getPlans: () => api.get('/payments/plans'),
  createCheckout: (lookupKey, originUrl) =>
    api.post('/payments/checkout', { lookup_key: lookupKey, origin_url: originUrl }),
  openPortal: (returnUrl) => api.post('/payments/portal', { return_url: returnUrl }),
  getStatus: (sessionId) => api.get(`/payments/status/${sessionId}`),
  // Platform admin — assisted sales
  generateLeadPaymentLink: (leadId, lookupKey) =>
    api.post(`/platform/leads/${leadId}/payment_link`, { lookup_key: lookupKey }),
};

// Invitations API
export const invitationsAPI = {
  // Admin
  list: (includeAll = false) => api.get('/invitations', { params: { include_all: includeAll } }),
  create: (data) => api.post('/invitations', data),
  revoke: (id) => api.delete(`/invitations/${id}`),
  resend: (id) => api.post(`/invitations/${id}/resend`),
  // Public (unauthenticated — token is the auth)
  lookup: (token) => api.get('/invitations/lookup', { params: { token } }),
  accept: (data) => api.post('/invitations/accept', data),
};

// Break-Glass (platform-admin only)
export const breakGlassAPI = {
  start: (data) => api.post('/platform/break-glass/start', data),
  active: () => api.get('/platform/break-glass/active'),
  end: () => api.post('/platform/break-glass/end'),
  list: (params = {}) => api.get('/platform/break-glass/sessions', { params }),
  revoke: (id) => api.post(`/platform/break-glass/${id}/revoke`),
};

// Users API (Admin)
export const usersAPI = {
  list: () => api.get('/users'),
  create: (data) => api.post('/users', null, { params: data }),
  updateRole: (userId, role) => api.put(`/users/${userId}/role`, null, { params: { role } }),
  resetPassword: (userId, newPassword) => api.put(`/users/${userId}/reset-password`, null, { params: { new_password: newPassword } }),
  delete: (userId) => api.delete(`/users/${userId}`),
};

// CSV Export API
export const exportAPI = {
  getCSVUrl: (exportType, dateFrom, dateTo) => {
    const params = new URLSearchParams();
    if (dateFrom) params.append('date_from', dateFrom);
    if (dateTo) params.append('date_to', dateTo);
    return `${BACKEND_URL}/api/export/csv/${exportType}${params.toString() ? '?' + params.toString() : ''}`;
  },
};

// Cost Report API
export const costReportAPI = {
  get: (params) => api.get('/reports/cost-report', { params }),
};

// Medics API
export const medicsAPI = {
  create: (data) => api.post('/medics', data),
  list: (params) => api.get('/medics', { params }),
  get: (id) => api.get(`/medics/${id}`),
  update: (id, data) => api.put(`/medics/${id}`, data),
  delete: (id) => api.delete(`/medics/${id}`),
  addDocument: (id, data) => api.post(`/medics/${id}/documents`, data),
  setAvailability: (id, data) => api.post(`/medics/${id}/availability`, data),
  getMyProfile: () => api.get('/medics/me/profile'),
};

// Shifts API
export const shiftsAPI = {
  create: (data) => api.post('/shifts', data),
  createBulk: (data) => api.post('/shifts/bulk', data),
  list: (params) => api.get('/shifts', { params }),
  get: (id) => api.get(`/shifts/${id}`),
  update: (id, data) => api.put(`/shifts/${id}`, data),
  delete: (id) => api.delete(`/shifts/${id}`),
  bulkDelete: (ids) => api.post('/shifts/bulk-delete', { shift_ids: ids }),
  bulkAssign: (data) => api.post('/shifts/bulk-assign', data),
  accept: (id) => api.post(`/shifts/${id}/accept`),
  decline: (id) => api.post(`/shifts/${id}/decline`),
  markNoShow: (id) => api.post(`/shifts/${id}/mark-no-show`),
  workload: (medicId, weekOf) => api.get(`/medics/${medicId}/workload`, { params: weekOf ? { week_of: weekOf } : undefined }),
  // Recurring shifts
  previewRecurring: (data) => api.post('/shifts/recurring/preview', data),
  createRecurring: (data) => api.post('/shifts/recurring', data),
};

// Expenses API
export const expensesAPI = {
  create: (data) => api.post('/expenses', data),
  list: (params) => api.get('/expenses', { params }),
  update: (id, data) => api.put(`/expenses/${id}`, data),
  uploadReceipt: (expenseId, file) => {
    const formData = new FormData();
    formData.append('expense_id', expenseId);
    formData.append('file', file);
    return api.post('/expenses/upload-receipt', formData, {
      headers: { 'Content-Type': 'multipart/form-data' }
    });
  },
  getReceiptUrl: (expenseId) => `${BACKEND_URL}/api/expenses/${expenseId}/receipt`,
};

// Rates API
export const ratesAPI = {
  create: (data) => api.post('/rates', data),
  list: () => api.get('/rates'),
  delete: (id) => api.delete(`/rates/${id}`),
};

// Shift Templates API
export const templatesAPI = {
  create: (data) => api.post('/shift-templates', data),
  list: () => api.get('/shift-templates'),
  update: (id, data) => api.put(`/shift-templates/${id}`, data),
  delete: (id) => api.delete(`/shift-templates/${id}`),
};

// Audit Logs API
export const auditAPI = {
  list: (params) => api.get('/audit-logs', { params }),
  getDetail: (id) => api.get(`/audit-logs/${id}`),
  getStats: (days) => api.get('/audit-logs/stats', { params: { days } }),
  exportUrl: (params) => {
    const queryString = new URLSearchParams(params).toString();
    return `${BACKEND_URL}/api/audit-logs/export?${queryString}`;
  },
};

export const systemAPI = {
  getSettings: () => api.get('/system/settings'),
  getBranding: () => api.get('/system/branding'),
  updateSettings: (data) => api.put('/system/settings', data),
  uploadLogo: (file) => {
    const formData = new FormData();
    formData.append('file', file);
    return api.post('/system/upload-logo', formData, {
      headers: { 'Content-Type': 'multipart/form-data' }
    });
  },
  deleteLogo: () => api.delete('/system/logo'),
  exportData: (data) => api.post('/system/export', data, { responseType: 'blob' }),
  triggerBackup: () => api.post('/system/backup'),
  clearTestData: () => api.delete('/system/test-data'),
  getStats: () => api.get('/system/stats'),
  sendWeeklyDigest: () => api.post('/system/send-weekly-digest'),
};

// Organisation API (currency, profile)
export const organisationAPI = {
  get: () => api.get('/organisation'),
  updateCurrency: (default_currency) =>
    api.put('/organisation/currency', { default_currency }),
  listCurrencies: () => api.get('/currencies'),
};

// Dashboard API
export const dashboardAPI = {
  getStats: () => api.get('/dashboard/stats'),
  getShiftCoverage: (days) => api.get('/dashboard/shift-coverage', { params: { days } }),
  getComplianceSummary: () => api.get('/dashboard/compliance-summary'),
  getFinanceSummary: () => api.get('/dashboard/finance-summary'),
};

// Contracts API
export const contractsAPI = {
  create: (data) => api.post('/contracts', data),
  list: () => api.get('/contracts'),
  get: (id) => api.get(`/contracts/${id}`),
  update: (id, data) => api.put(`/contracts/${id}`, data),
  delete: (id) => api.delete(`/contracts/${id}`),
};

// Job Types API
export const jobTypesAPI = {
  create: (data) => api.post('/job-types', data),
  list: (params) => api.get('/job-types', { params }),
  get: (id) => api.get(`/job-types/${id}`),
  update: (id, data) => api.put(`/job-types/${id}`, data),
  delete: (id) => api.delete(`/job-types/${id}`),
};

// Assignment Check API
export const assignmentAPI = {
  checkAssignment: (data) => api.post('/shifts/check-assignment', data),
  logWarningAction: (data) => api.post('/shifts/log-warning-action', data),
};

// Medic Portal API
export const medicPortalAPI = {
  // Shifts
  getUpcomingShifts: () => api.get('/medic/shifts/upcoming'),
  getPastShifts: () => api.get('/medic/shifts/past'),
  getShiftDetails: (id) => api.get(`/medic/shifts/${id}`),
  
  // Availability
  getAvailability: (params) => api.get('/medic/availability', { params }),
  setAvailability: (data) => api.post('/medic/availability', data),
  
  // Open Shifts & Requests
  getOpenShifts: (params) => api.get('/medic/open-shifts', { params }),
  requestShift: (data) => api.post('/medic/request-shift', data),
  getMyRequests: () => api.get('/medic/requests'),
  
  // Profile
  getProfile: () => api.get('/medic/profile'),

  // Earnings (iter64) — expected + actual totals in one call. The medic
  // portal reads this to render the Earnings pane; per-shift
  // `rate_payable` on the regular shift endpoints is unchanged.
  getEarnings: () => api.get('/medic/earnings'),
};

// Scheduler Shift Requests API
export const shiftRequestsAPI = {
  list: (params) => api.get('/shift-requests', { params }),
  approve: (id, force = false) => api.put(`/shift-requests/${id}/approve?force=${force}`),
  decline: (id) => api.put(`/shift-requests/${id}/decline`),
  getMedicAvailability: (params) => api.get('/scheduler/medic-availability', { params }),
};

export const leaveAPI = {
  list: (params) => api.get('/leave-requests', { params }),
  create: (data) => api.post('/leave-requests', data),
  approve: (id, notes) => api.put(`/leave-requests/${id}/approve`, { notes }),
  decline: (id, notes) => api.put(`/leave-requests/${id}/decline`, { notes }),
  cancel: (id) => api.put(`/leave-requests/${id}/cancel`),
  myBalance: (year) => api.get('/leave-balance', { params: year ? { year } : undefined }),
  medicBalance: (medicId, year) => api.get(`/medics/${medicId}/leave-balance`, { params: year ? { year } : undefined }),
};

// Warning Types (must match backend)
export const WARNING_TYPES = {
  EXPIRED: 'expired',
  EXPIRING_SOON: 'expiring_soon',
  JOB_TYPE_NOT_APPROVED: 'job_type_not_approved',
};

export const WARNING_ACTIONS = {
  VIEWED: 'viewed',
  ACKNOWLEDGED: 'acknowledged',
  CANCELLED: 'cancelled',
};

// Dashboard API (extended)
export const dashboardExtendedAPI = {
  getShiftsByJobType: () => api.get('/dashboard/shifts-by-job-type'),
  getShiftsByContract: () => api.get('/dashboard/shifts-by-contract'),
  getMedicsPerContract: () => api.get('/dashboard/medics-per-contract'),
  getDocumentExpirySummary: () => api.get('/dashboard/document-expiry-summary'),
};

// Integration API
export const integrationAPI = {
  exportPowerBI: (params) => api.get('/integrations/powerbi/export', { params }),
  getSQLSchema: () => api.get('/integrations/sql-server/schema'),
};

// Document Types API (Admin)
export const documentTypesAPI = {
  list: () => api.get('/document-types'),
  create: (data) => api.post('/document-types', data),
  update: (id, data) => api.put(`/document-types/${id}`, data),
  delete: (id) => api.delete(`/document-types/${id}`),
};

// Documents API
export const documentsAPI = {
  upload: (formData) => api.post('/documents/upload', formData, {
    headers: { 'Content-Type': 'multipart/form-data' }
  }),
  getByMedic: (medicId, includeHistory = false) => api.get(`/documents/medic/${medicId}`, {
    params: { include_history: includeHistory }
  }),
  get: (id) => api.get(`/documents/${id}`),
  download: (id) => api.get(`/documents/${id}/download`, { responseType: 'blob' }),
  review: (id, data) => api.put(`/documents/${id}/review`, data),
  retype: (id, documentTypeId) => api.put(`/documents/${id}/retype`, { document_type_id: documentTypeId }),
  delete: (id) => api.delete(`/documents/${id}`),
  getPending: () => api.get('/documents/pending'),
  getHistory: (docId) => api.get(`/documents/${docId}/history`),
};

// Compliance API
export const complianceAPI = {
  getConfig: () => api.get('/compliance/config'),
  updateConfig: (data) => api.put('/compliance/config', data),
  getOnboardingStatus: (medicId) => api.get(`/compliance/onboarding/${medicId}`),
  getMyStatus: () => api.get('/compliance/my-status'),
  updateMedicStatus: (medicId, data) => api.put(`/compliance/medics/${medicId}/status`, data),
  getMedics: (params) => api.get('/compliance/medics', { params }),
  checkSchedule: (medicId) => api.get(`/compliance/check-schedule/${medicId}`),
  seedDocumentTypes: () => api.post('/compliance/seed-document-types'),
  exportCSV: () => api.get('/compliance/export', { responseType: 'blob' }),
};

// Notifications API
export const notificationsAPI = {
  getConfig: () => api.get('/notifications/config'),
  preview: (days = 30) => api.get('/notifications/preview', { params: { days } }),
  sendExpiryAlerts: (data) => api.post('/notifications/send-expiry-alerts', data),
  sendTestEmail: (data) => api.post('/notifications/test-email', data),
  runScheduledCheck: () => api.post('/notifications/run-scheduled-check'),
  getHistory: (limit = 50) => api.get('/notifications/history', { params: { limit } }),
  sendReminder: (medicId) => api.post(`/notifications/send-reminder/${medicId}`),
};

// GPS Tracking API
export const trackingAPI = {
  // Settings
  getSettings: () => api.get('/tracking/settings'),
  updateGeofenceSettings: (data) => api.put('/tracking/settings/geofence', data),
  
  // Check-in/out
  checkIn: (shiftId, data) => api.post(`/tracking/shifts/${shiftId}/check-in`, data),
  checkOut: (shiftId, data) => api.post(`/tracking/shifts/${shiftId}/check-out`, data),
  
  // Active medics (for live map)
  getActiveMedics: () => api.get('/tracking/active-medics'),
  
  // Location history
  getShiftLocationHistory: (shiftId) => api.get(`/tracking/shifts/${shiftId}/location-history`),
  
  // Update shift coordinates (for geofencing)
  updateShiftCoords: (shiftId, data) => api.put(`/tracking/shifts/${shiftId}/location-coords`, data),
  
  // My status (for medics)
  getMyStatus: () => api.get('/tracking/my-status'),
};

// Push Notifications API
export const pushNotificationsAPI = {
  // VAPID key for browser push
  getVapidKey: () => api.get('/push-notifications/vapid-public-key'),
  
  // Push subscription management
  subscribe: (data) => api.post('/push-notifications/subscribe', data),
  unsubscribe: (endpoint) => api.delete('/push-notifications/unsubscribe', { params: { endpoint } }),
  
  // In-app notifications
  getInApp: (limit = 50, includeRead = true) => 
    api.get('/push-notifications/in-app', { params: { limit, include_read: includeRead } }),
  getUnreadCount: () => api.get('/push-notifications/unread-count'),
  markRead: (notificationId) => api.put(`/push-notifications/${notificationId}/read`),
  markAllRead: () => api.put('/push-notifications/mark-all-read'),
  deleteNotification: (notificationId) => api.delete(`/push-notifications/${notificationId}`),
  clearAll: () => api.delete('/push-notifications/clear-all'),
  
  // Preferences
  getPreferences: () => api.get('/push-notifications/preferences'),
  updatePreferences: (data) => api.put('/push-notifications/preferences', data),
  
  // Test
  sendTest: () => api.post('/push-notifications/test'),
};

// Platform Admin API — Phase A (metadata only, cross-tenant)
export const platformAPI = {
  me: () => api.get('/platform/me'),
  metrics: () => api.get('/platform/metrics'),
  listOrganisations: (params = {}) => api.get('/platform/organisations', { params }),
  getOrganisation: (orgId) => api.get(`/platform/organisations/${orgId}`),
  listAdmins: () => api.get('/platform/admins'),
  searchUsers: (q, limit = 20) => api.get('/platform/users/search', { params: { q, limit } }),
  grantAdmin: (userId) => api.post(`/platform/admins/${userId}/grant`),
  revokeAdmin: (userId) => api.post(`/platform/admins/${userId}/revoke`),
  audit: (limit = 200) => api.get('/platform/audit', { params: { limit } }),
  // Trial leads
  listLeads: (params = {}) => api.get('/platform/leads', { params }),
  updateLeadStatus: (leadId, newStatus) =>
    api.put(`/platform/leads/${leadId}/status`, null, { params: { new_status: newStatus } }),
  leadsExportUrl: (status) => {
    const qs = status ? `?status=${encodeURIComponent(status)}` : '';
    return `${BACKEND_URL}/api/platform/leads/export${qs}`;
  },
};

export default api;
